
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getUserGymId } from '@/utils/gymAuth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClassModeSelect } from "./class-dialog/ClassModeSelect";
import { ClassNameField } from "./class-dialog/ClassNameField";
import { toast } from "sonner";
import i18n from "@/i18n";

interface AddClassDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialDate: Date;
  initialTime: string;
  onSave: (classData: any) => Promise<boolean>;
  /** Calendrier actuellement sélectionné dans le planning (présélection dans le formulaire) */
  initialCalendarId?: string | null;
}

const baseFormSchema = z.object({
  mode: z.enum(["existing", "new"]).default("existing"),
  name: z.string().min(2, {
    message: "Class name must be at least 2 characters.",
  }),
  // Le coach était codé en dur à « Default Instructor » côté soumission : la
  // valeur remontait telle quelle dans la vue Jour, les présences et les
  // exports. Il est désormais saisi (liste des coachs de la salle en
  // suggestion, saisie libre autorisée pour un intervenant extérieur).
  instructor: z.string().min(2, {
    message: i18n.t("addClass.instructorMinLength", { ns: "bookingui" }),
  }),
  capacity: z.coerce.number().min(1, {
    message: "Capacity must be at least 1 person",
  }),
  duration: z.string().min(1, {
    message: "Duration must be specified",
  }),
  time: z.string().min(1, {
    message: "Time must be specified",
  }),
  category: z.string().min(1, {
    message: "Category must be specified",
  }),
  categoryId: z.string().min(1, {
    message: "Category ID must be specified",
  }),
  courseId: z.string().optional(),
  // Optionnel dans le schéma de base : une salle peut n'avoir aucun planning
  // configuré (le champ n'est alors pas affiché). L'obligation est réintroduite
  // dynamiquement ci-dessous dès qu'au moins un calendrier existe.
  calendarId: z.string().optional(),
});

const formSchema = baseFormSchema;

/**
 * Le calendrier n'est requis que si la salle en possède au moins un.
 * Sans cette condition, les salles sans planning configuré ne pouvaient plus
 * créer de séance : la validation échouait sur un champ jamais rendu.
 */
const buildFormSchema = (requireCalendar: boolean) =>
  requireCalendar
    ? baseFormSchema.refine((v) => !!v.calendarId, {
        path: ["calendarId"],
        message: i18n.t("addClass.calendarRequired", { ns: "bookingui" }),
      })
    : baseFormSchema;


export function AddClassDialog({
  isOpen,
  onOpenChange,
  initialDate,
  initialTime,
  onSave,
  initialCalendarId = null,
}: AddClassDialogProps) {
  const { t } = useTranslation(["schedule", "common", "bookingui"]);
  const gymId = getUserGymId();
  const [courses, setCourses] = useState<Array<{ id: string; name: string; category_id?: string|null; default_capacity?: number; default_duration?: number; calendar_ids?: string[] | null }>>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [calendars, setCalendars] = useState<Array<{ id: string; name: string; color?: string | null }>>([]);
  const [coachNames, setCoachNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = useMemo(
    () => buildFormSchema(calendars.length > 0),
    [calendars.length]
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(validationSchema),

    defaultValues: {
      mode: "existing",
      name: "",
      instructor: "",
      capacity: 10,
      duration: "60",
      time: initialTime,
      category: "",
      categoryId: "",
      courseId: "",
      calendarId: "",
    },
  });
  
  // Brouillon persisté. Ce dialog reseed ses champs par plusieurs vagues de
  // setValue asynchrones à l'ouverture : la restauration est appliquée À LA
  // FIN de l'effet fetchCourses (après la vague), via mergeDraftOver.
  // `time` est exclu : lecture seule, piloté par le créneau cliqué.
  const { clearDraft, mergeDraftOver } = useFormDraft<z.infer<typeof formSchema>>(
    "add-class",
    form,
    {
      restoreOnMount: false,
      exclude: ["time"],
      isEmpty: (v) => !v.name && !v.courseId && !v.instructor,
    }
  );

  // Update time field when initialTime changes
  useEffect(() => {
    if (initialTime) {
      form.setValue('time', initialTime);
    }
  }, [initialTime, form]);
  
  useEffect(() => {
    if (!isOpen) return;

    if (initialCalendarId) {
      form.setValue("calendarId", initialCalendarId);
      return;
    }

    if (calendars.length === 1) {
      form.setValue("calendarId", calendars[0].id);
      return;
    }

    form.setValue("calendarId", "");
  }, [initialCalendarId, isOpen, form, calendars]);

  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      try {
        const { getCourses, getCategories } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
        const { listCalendars } = await import('@/services/edgeFunctionWrappers/calendarsWrapper');

        const coursesData = await getCourses(gymId);
        setCourses(coursesData || []);

        const categoriesData = await getCategories(gymId);
        setCategories(categoriesData || []);

        const calendarsData = await listCalendars(gymId ?? "", false);
        setCalendars(calendarsData || []);

        // Suggestions de coachs pour le champ « Coach ».
        try {
          const { getAllCoaches } = await import('@/store/api/coaches/coachFetchOperations');
          const coachesData = await getAllCoaches();
          setCoachNames(
            (coachesData || [])
              .map((c) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim())
              .filter(Boolean)
          );
        } catch (coachError) {
          // La liste des coachs n'est qu'une aide à la saisie : son échec ne
          // doit pas empêcher la création d'une séance.
          console.error('Error fetching coaches for instructor field:', coachError);
        }


        // Set default values if we have data
        if (coursesData && coursesData.length > 0) {
          form.setValue('courseId', coursesData[0].id);
          form.setValue('name', coursesData[0].name); // Set the name for existing course mode
          
          if (coursesData[0].category_id) {
            form.setValue('categoryId', coursesData[0].category_id);
            
            // Find and set category name
            const category = categoriesData?.find(cat => cat.id === coursesData[0].category_id);
            if (category) form.setValue('category', category.name);
          }
          
          if (coursesData[0].default_capacity) form.setValue('capacity', coursesData[0].default_capacity);
          if (coursesData[0].default_duration) form.setValue('duration', coursesData[0].default_duration.toString());
        }

        // Restauration du brouillon APRÈS la vague de setValue ci-dessus :
        // la frappe interrompue gagne sur les valeurs par défaut du premier
        // cours et sur le blanchiment de calendarId. Sans brouillon, ce reset
        // de getValues() est un no-op pristine.
        form.reset(mergeDraftOver(form.getValues()));
      } catch (error) {
        console.error('Error fetching courses and categories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      fetchCourses();
    }
    // gymId volontairement hors deps (comportement historique conservé) ;
    // mergeDraftOver est stable.
  }, [isOpen, form, mergeDraftOver]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Cours proposés selon le planning sélectionné. Règle (cf. migration
  // course_calendars) : un cours SANS affectation est « universel » → proposé
  // sur tous les plannings ; un cours affecté n'apparaît que sur ses plannings.
  // Sans planning choisi, on montre tout.
  const watchedCalendarId = form.watch("calendarId");
  const coursesForCalendar = useMemo(() => {
    if (!watchedCalendarId) return courses;
    return courses.filter((c) => {
      const ids = c.calendar_ids;
      if (!ids || ids.length === 0) return true; // universel
      return ids.includes(watchedCalendarId);
    });
  }, [courses, watchedCalendarId]);

  // Si le cours actuellement choisi n'est pas proposé sur le planning
  // sélectionné, on le réinitialise pour éviter une incohérence silencieuse.
  // On remet AUSSI catégorie/capacité/durée aux valeurs neutres : ces champs
  // sont remplis par `handleCourseSelect`, et ses gardes de vérité (`if
  // (course.default_capacity)`, `if (course.category_id)`) ne les écrasent pas
  // toujours au prochain choix — les valeurs de l'ancien cours persisteraient.
  useEffect(() => {
    const current = form.getValues("courseId");
    if (current && !coursesForCalendar.some((c) => c.id === current)) {
      form.setValue("courseId", "");
      form.setValue("name", "");
      form.setValue("categoryId", "");
      form.setValue("category", "");
      form.setValue("capacity", 10);
      form.setValue("duration", "60");
    }
  }, [coursesForCalendar, form]);

  // Handle course selection to populate default values
  const handleCourseSelect = (courseId: string) => {
    const selectedCourse = courses.find(course => course.id === courseId);
    if (!selectedCourse) return;
    
    form.setValue('courseId', courseId);
    form.setValue('name', selectedCourse.name); // Important: set the name field with the course name
    
    // Update category if available
    if (selectedCourse.category_id) {
      form.setValue('categoryId', selectedCourse.category_id);
      
      // Find and set category name
      const category = categories.find(cat => cat.id === selectedCourse.category_id);
      if (category) form.setValue('category', category.name);
    }
    
    // Update capacity and duration if available
    if (selectedCourse.default_capacity) {
      form.setValue('capacity', selectedCourse.default_capacity);
    }
    
    if (selectedCourse.default_duration) {
      form.setValue('duration', selectedCourse.default_duration.toString());
    }
  };
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      // Format the date
      const dateString = initialDate.toISOString().split('T')[0];

      const instructor = values.instructor.trim();

      let finalCourseId: string | undefined = undefined;
      
      // Si mode "new", créer le cours via l'edge function
      if (values.mode === 'new') {
        const gymId = getUserGymId();
        
        if (!gymId) {
          toast.error(t("common.gymIdNotFound", { ns: "bookingui" }));
          return;
        }
        
        const { data: response, error: courseError } = await supabase.functions.invoke(
          'classes-management',
          {
            body: {
              action: 'create_course',
              gym_id: gymId,
              courseData: {
                name: values.name,
                category_id: values.categoryId,
                default_capacity: values.capacity,
                default_duration: parseInt(values.duration, 10)
              }
            }
          }
        );
        
        if (courseError || !response?.course) {
          console.error("Erreur création cours:", courseError);
          toast.error(t("common.courseCreateError", { ns: "bookingui" }));
          return;
        }
        
        finalCourseId = response.course.id;
        toast.success(t("common.courseCreateSuccess", { ns: "bookingui" }));
      } else {
        // Mode "existing"
        finalCourseId = values.courseId;
      }
      
      if (!finalCourseId) {
        toast.error(t("addClass.courseRequired", { ns: "bookingui" }));
        return;
      }
      
      const classData = {
        name: values.name,
        class_date: dateString,
        time: values.time,
        duration: values.duration,
        category: values.categoryId,
        capacity: values.capacity,
        instructor: instructor,
        courseId: finalCourseId,
        calendarId: values.calendarId ?? undefined
      };
      
      // Save the data
      const success = await onSave(classData);
      
      if (success) {
        toast.success(t('classAddedSuccess', { ns: 'schedule' }));
        clearDraft();
        form.reset();
        onOpenChange(false);
      } else {
        toast.error(t('errorAddingClass', { ns: 'common', defaultValue: 'Failed to add class' }));
      }
    } catch (error) {
      console.error('Error submitting class data:', error);
      toast.error(t('errorAddingClass', { ns: 'common', defaultValue: 'Failed to add class' }));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("addClass", { ns: "schedule" })}</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, (errors) => {
              // Sans ce retour, un champ invalide non rendu (ex. calendrier)
              // bloquait la création sans aucun message pour l'utilisateur.
              console.warn("[AddClassDialog] Validation errors:", errors);
              const first = Object.values(errors)[0] as { message?: string } | undefined;
              toast.error(
                first?.message ||
                  t("errorAddingClass", { ns: "common", defaultValue: "Failed to add class" })
              );
            })}
            className="space-y-4"
          >

            {/* Mode selection: existing class or new class */}
            <ClassModeSelect form={form} />
            
            {/* Class name field - changes behavior based on mode */}
            <ClassNameField
              form={form}
              courses={coursesForCalendar}
              onCourseSelect={handleCourseSelect}
            />
            
            <FormField
              control={form.control}
              name="instructor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("instructor", { ns: "common", defaultValue: "Coach" })}</FormLabel>
                  <FormControl>
                    <Input
                      list="add-class-coach-suggestions"
                      placeholder={t("instructorPlaceholder", { ns: "common", defaultValue: "Nom du coach" })}
                      {...field}
                    />
                  </FormControl>
                  <datalist id="add-class-coach-suggestions">
                    {coachNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="capacity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("capacity", { ns: "common" })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder={t("capacityPlaceholder", { ns: "common" })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("duration", { ns: "common" })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder={t("durationPlaceholder", { ns: "common" })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("time", { ns: "common" })}</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t("timePlaceholder", { ns: "common" })} 
                      {...field} 
                      readOnly 
                      className="bg-muted"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("category", { ns: "common" })}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      const selectedCategory = categories.find(c => c.id === value);
                      if (selectedCategory) {
                        form.setValue("category", selectedCategory.name);
                        form.setValue("categoryId", selectedCategory.id);
                      }
                    }}
                    value={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("categoryPlaceholder", { ns: "common" })} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {calendars.length > 0 && (
              <FormField
                control={form.control}
                name="calendarId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("calendar", { ns: "common", defaultValue: "Calendrier" })}</FormLabel>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(v) => form.setValue("calendarId", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("common:calendar_placeholder", "Sélectionner un calendrier")} />
                      </SelectTrigger>
                      <SelectContent>
                        {calendars.map((cal) => (
                          <SelectItem key={cal.id} value={cal.id}>
                            {cal.color && (
                              <span
                                className="mr-2 inline-block h-3 w-3 rounded-full"
                                style={{ backgroundColor: cal.color }}
                              />
                            )}
                            {cal.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <Button type="submit" variant="default" disabled={isLoading}>
              {isLoading ? t("saving", { ns: "common" }) : t("add", { ns: "common" })}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
